import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib";
import { type AccessPermission, type ExtensionConsent, integrationsService } from "@/lib/services";
import { ACCESS_PERMISSIONS, ExtensionConsentRow } from "./ExtensionConsentRow";

/** One save in flight. A card can save its read and write choices at the same time. */
function busyKey(slug: string, permission: AccessPermission): string {
  return `${slug}:${permission}`;
}

/** The extension with *permission* back on its default (the server removed the user's choice). */
function withDefault(ext: ExtensionConsent, permission: AccessPermission): ExtensionConsent {
  const consent = ext[permission];
  const reset = { ...consent, hasExplicitPreference: false, effectiveEnabled: consent.consentMode === "opt_out" };
  return permission === "read" ? { ...ext, read: reset } : { ...ext, write: reset };
}

export function SettingsExtensionsTab() {
  const { showToast } = useToast();
  const [extensions, setExtensions] = useState<ExtensionConsent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await integrationsService.listExtensionConsent();
      setExtensions(res.data);
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to load extensions"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const startSaving = (key: string) => setBusy((prev) => new Set(prev).add(key));
  const endSaving = (key: string) => setBusy((prev) => new Set([...prev].filter((k) => k !== key)));

  const handleToggle = async (slug: string, permission: AccessPermission, enabled: boolean) => {
    const key = busyKey(slug, permission);
    try {
      startSaving(key);
      const res = await integrationsService.setExtensionPreference(slug, permission, enabled);
      // Merge only this class: the response can predate a save of the other class that already landed.
      setExtensions((prev) =>
        prev.map((ext) => (ext.slug === slug ? { ...ext, [permission]: res.data[permission] } : ext)),
      );
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to save your choice"), "error");
    } finally {
      endSaving(key);
    }
  };

  const handleReset = async (slug: string, permission: AccessPermission) => {
    const key = busyKey(slug, permission);
    try {
      startSaving(key);
      await integrationsService.clearExtensionPreference(slug, permission);
      setExtensions((prev) => prev.map((ext) => (ext.slug === slug ? withDefault(ext, permission) : ext)));
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to reset to the default"), "error");
    } finally {
      endSaving(key);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-content-primary">Extensions</h2>
        <p className="text-sm text-content-tertiary mt-0.5">
          Choose what AnthmAI may read or do in each service. Changes apply from your next message.
        </p>
      </div>
      <ExtensionsList
        loading={loading}
        extensions={extensions}
        busy={busy}
        onToggle={handleToggle}
        onReset={handleReset}
      />
    </div>
  );
}

interface ExtensionsListProps {
  loading: boolean;
  extensions: ExtensionConsent[];
  busy: ReadonlySet<string>;
  onToggle: (slug: string, permission: AccessPermission, enabled: boolean) => void;
  onReset: (slug: string, permission: AccessPermission) => void;
}

function ExtensionsList({ loading, extensions, busy, onToggle, onReset }: Readonly<ExtensionsListProps>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (extensions.length === 0) {
    return (
      <p className="text-sm text-content-tertiary py-6">
        No extensions need your consent. An administrator can enable integrations.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {extensions.map((ext) => (
        <ExtensionConsentRow
          key={ext.slug}
          extension={ext}
          busyPermissions={new Set(ACCESS_PERMISSIONS.filter((permission) => busy.has(busyKey(ext.slug, permission))))}
          onToggle={onToggle}
          onReset={onReset}
        />
      ))}
    </div>
  );
}
